import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { companyService } from '../../services'
import type { CreateCompanyRequest } from '../../services/api.service'
import {
  FormContainer,
  FormInput,
  FormTextarea,
  FormColorPicker,
  FormCheckbox,
  FormActions,
} from '../../components/forms'
import PageLayout from '../../components/layout/page-layout'
import { Container } from '../../components/ui/container'
import { FEATURES } from '../../config/features.config'

const AVAILABLE_MODULES = [
  { key: 'onboarding', labelKey: 'onboarding' },
  { key: 'weiterbildung', labelKey: 'weiterbildung' },
  { key: 'time_tracking', labelKey: 'timeTracking' },
  { key: 'absence_requests', labelKey: 'absenceRequests' },
] as const

type CompanyModuleKey = (typeof AVAILABLE_MODULES)[number]['key']

const DEFAULT_ENABLED_MODULES: CompanyModuleKey[] = [
  'onboarding',
  'weiterbildung',
  ...(FEATURES.timeTracking ? (['time_tracking'] as const) : []),
  'absence_requests',
]

export default function CompanyForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6366f1',
    logoUrl: '',
    isActive: true,
    salesStatus: 'lead' as const,
    isApproved: false,
    maxLicenses: '' as string | number,
  })
  const [enabledModules, setEnabledModules] =
    useState<CompanyModuleKey[]>(DEFAULT_ENABLED_MODULES)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [logoError, setLogoError] = useState<string | undefined>()

  const moduleOptions = useMemo(
    () =>
      AVAILABLE_MODULES.filter(
        mod => mod.key !== 'time_tracking' || FEATURES.timeTracking
      ).map(mod => ({
        ...mod,
        label: t(`companies.enabledModules.${mod.labelKey}`),
      })),
    [t]
  )

  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', id],
    queryFn: () => companyService.getCompanyById(id!),
    enabled: isEdit,
  })

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        description: company.description || '',
        color: company.color || '#6366f1',
        logoUrl: (company as any).logoUrl || '',
        isActive: company.isActive,
        salesStatus: (company.salesStatus as any) || 'lead',
        isApproved: (company as any).isApproved ?? false,
        maxLicenses: (company as any).maxLicenses ?? '',
      })
      if ((company as any).enabledModules) {
        setEnabledModules((company as any).enabledModules)
      }
    }
  }, [company])

  const createMutation = useMutation({
    mutationFn: (data: CreateCompanyRequest) => companyService.createCompany(data),
    onSuccess: () => {
      navigate('/companies')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => companyService.updateCompany(id, data),
    onSuccess: () => {
      navigate('/companies')
    },
  })

  const approvalMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      companyService.updateApproval(id, data),
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const { isApproved, maxLicenses, ...rest } = formData
    const submitData = {
      ...rest,
      enabledModules,
      maxLicenses: maxLicenses === '' ? undefined : Number(maxLicenses),
    }

    if (isEdit) {
      updateMutation.mutate(
        { id: id!, data: submitData },
        {
          onSuccess: async () => {
            if (company && (company as any).isApproved !== isApproved) {
              await approvalMutation.mutateAsync({
                id: id!,
                data: {
                  isApproved,
                  maxLicenses: maxLicenses === '' ? undefined : Number(maxLicenses),
                },
              })
            }
            navigate('/companies')
          },
        }
      )
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }))
  }

  const handleColorChange = (color: string) => {
    setFormData(prev => ({ ...prev, color }))
  }

  const handleModuleToggle = (moduleKey: CompanyModuleKey) => {
    setEnabledModules(prev =>
      prev.includes(moduleKey) ? prev.filter(m => m !== moduleKey) : [...prev, moduleKey]
    )
  }

  const handleLogoUpload = async (file: File) => {
    if (!file) return

    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml',
    ]
    if (!allowedTypes.includes(file.type)) {
      setLogoError(t('common.invalidFileType', { types: allowedTypes.join(', ') }))
      return
    }

    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      setLogoError(t('common.fileTooLarge10mb'))
      return
    }

    setUploadingLogo(true)
    setLogoError(undefined)

    try {
      const result = await companyService.uploadLogo(file)
      setFormData(prev => ({ ...prev, logoUrl: result.logoUrl }))
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.userMessage ||
        err?.message ||
        t('errors.uploadLogo')
      setLogoError(errorMessage)
    } finally {
      setUploadingLogo(false)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <PageLayout
      headerProps={{
        title: isEdit ? t('companies.editTitle') : t('companies.createTitle'),
        subtitle: isEdit ? t('companies.editTitle') : t('companies.createTitle'),
        variant: 'dashboard',
      }}
      isLoading={isLoadingCompany || isLoading}
      error={createMutation.error || updateMutation.error}
      loadingMessage={isEdit ? t('companies.loading') : t('companies.createCompany')}
      errorMessage={t('companies.loadError')}
    >
      <Container>
        <FormContainer
          title={isEdit ? t('companies.editTitle') : t('companies.createTitle')}
          subtitle={isEdit ? t('companies.editTitle') : t('companies.createTitle')}
          onSubmit={handleSubmit}
        >
          <div className='space-y-6'>
            <FormInput
              label={t('companies.companyName')}
              name='name'
              value={formData.name}
              onChange={handleChange}
              placeholder='z.B. Acme Corporation'
              required
            />

            <FormTextarea
              label={t('companies.description')}
              name='description'
              value={formData.description}
              onChange={handleChange}
              placeholder={t('auth.companyDescriptionPlaceholder')}
            />

            <FormColorPicker
              label={t('companies.companyColor')}
              value={formData.color}
              onChange={handleColorChange}
            />

            <div className='space-y-2'>
              <label className='block text-sm font-medium text-gray-700 '>
                {t('companies.companyLogo')}
              </label>
              <div className='space-y-4'>
                <div>
                  <input
                    type='file'
                    accept='image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml'
                    onChange={e => {
                      const file = e.target.files?.[0]
                      if (file) {
                        handleLogoUpload(file)
                      }
                    }}
                    disabled={uploadingLogo}
                    className='block w-full text-sm text-gray-500 
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100 
                      disabled:opacity-50 disabled:cursor-not-allowed'
                  />
                  <p className='mt-1 text-xs text-gray-500 '>{t('common.allowedLogoFormats')}</p>
                </div>
                {uploadingLogo && (
                  <div className='flex items-center space-x-2 text-sm text-blue-600 '>
                    <svg
                      className='w-4 h-4 animate-spin'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                      />
                    </svg>
                    <span>{t('common.uploadingLogo')}</span>
                  </div>
                )}
                {logoError && <div className='text-sm text-red-600 '>{logoError}</div>}
                {formData.logoUrl && !uploadingLogo && (
                  <div className='space-y-2'>
                    <label className='block text-sm font-medium text-gray-700 '>
                      {t('common.logoPreview')}
                    </label>
                    <div className='flex items-center space-x-4'>
                      <img
                        src={formData.logoUrl}
                        alt={t('common.logoPreview')}
                        className='w-16 h-16 object-contain border border-gray-300 rounded-xl'
                        onError={e => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                      <div className='text-sm text-gray-600 '>{t('common.logoPreviewHint')}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <FormCheckbox
              label={t('companies.isActive')}
              name='isActive'
              checked={formData.isActive}
              onChange={handleChange}
            />

            {isEdit && (
              <div className='space-y-4 p-4 border border-gray-200 rounded-xl bg-gray-50'>
                <h3 className='text-sm font-semibold text-gray-800'>{t('companies.isApproved')}</h3>

                <FormCheckbox
                  label={t('companies.isApproved')}
                  name='isApproved'
                  checked={formData.isApproved}
                  onChange={handleChange}
                />

                <p className='text-xs text-gray-500'>{t('common.defaultValue')}</p>

                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-xs font-medium text-gray-600 mb-1'>
                      {t('companies.maxLicenses')}
                    </label>
                    <input
                      type='number'
                      name='maxLicenses'
                      min={0}
                      value={formData.maxLicenses}
                      onChange={handleChange}
                      placeholder={t('common.defaultValue')}
                      className='w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500'
                    />
                  </div>
                </div>
              </div>
            )}

            <div className='space-y-3'>
              <label className='block text-sm font-medium text-gray-700'>
                {t('companies.enabledModulesTitle')}
              </label>
              <p className='text-xs text-gray-500'>{t('companies.enabledModulesHint')}</p>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                {moduleOptions.map(mod => (
                  <label
                    key={mod.key}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                      enabledModules.includes(mod.key)
                        ? 'border-blue-300 bg-blue-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input
                      type='checkbox'
                      checked={enabledModules.includes(mod.key)}
                      onChange={() => handleModuleToggle(mod.key)}
                      className='w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                    />
                    <span className='text-sm font-medium text-gray-700'>{mod.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <FormActions
              onCancel={() => navigate('/companies')}
              submitText={isEdit ? t('common.saveChanges') : t('companies.createCompany')}
              isLoading={isLoading}
            />
          </div>
        </FormContainer>
      </Container>
    </PageLayout>
  )
}
